import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { apiGet, apiDelete, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import Pagination from "../Pagination";
import AuthContext from "context/AuthContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import helpers from "utils/helpers";

// Components
import ShiftList from "./ShiftList";
import ShiftAdd from "./ShiftAdd";
import ShiftEdit from "./ShiftEdit";
import ShiftCalendar from "./ShiftCalendar";
import ShiftFiltersHeader from "./ShiftFiltersHeader";
import LocationManage from "./LocationManage";
import SwapRequestsModal from "./SwapRequestsModal";
import TemplateManage from "./TemplateManage";

const EmployeeShiftScheduling = () => {
  const { updatePageName } = useContext(AuthContext);
  const { t } = useTranslation();
  
  // ═══ STATE MANAGEMENT ═══
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [shiftList, setShiftList] = useState([]);
  const [shiftAdd, setShiftAdd] = useState(false);
  const [shiftEdit, setShiftEdit] = useState(false);
  const [currentItem, setCurrentItem] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterData, setFilterData] = useState({
    locationId: "",
    status: "",
    searchKey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    shiftId: "",
    departmentId: ""
  });
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSwapRequests, setShowSwapRequests] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  
  // Calendar specific states
  const [calendarView, setCalendarView] = useState("weekly");
  const [calendarDate, setCalendarDate] = useState(dayjs());
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [allShiftsList, setAllShiftsList] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Add Shift Modal Pre-fill states
  const [selectedAddEmployee, setSelectedAddEmployee] = useState(null);
  const [selectedAddDate, setSelectedAddDate] = useState(null);
  const [selectedAddDepartment, setSelectedAddDepartment] = useState(null);

  // Colors Logic
  const pastelColors = useMemo(() => [
    '#E0F2FE', '#DCFCE7', '#FEF3C7', '#EDE9FE', '#FCE7F3', 
    '#FFEDD5', '#E0E7FF', '#CCFBF1', '#FEE2E2', '#F3E8FF'
  ], []);
  const shiftColorMap = useRef({});

  const getShiftColor = (shiftName) => {
    if (!shiftName) return '#F3F4F6';
    if (shiftColorMap.current[shiftName]) return shiftColorMap.current[shiftName];
    const usedColors = Object.values(shiftColorMap.current);
    const availableColors = pastelColors.filter(color => !usedColors.includes(color));
    const selectedColor = availableColors.length > 0 
      ? availableColors[Math.floor(Math.random() * availableColors.length)]
      : pastelColors[Math.floor(Math.random() * pastelColors.length)];
    shiftColorMap.current[shiftName] = selectedColor;
    return selectedColor;
  };

  // ═══ HANDLERS ═══
  const handleEdit = (item) => {
    setCurrentItem(item);
    setShiftEdit(!shiftEdit);
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  const handleCardClick = (shiftData, employee = null, date = null, departmentId = null) => {
    if (shiftData) {
      setCurrentItem(shiftData);
      setShiftEdit(true);
    } else {
      setSelectedAddEmployee(employee);
      setSelectedAddDate(date);
      setSelectedAddDepartment(departmentId);
      setShiftAdd(true);
    }
  };

  const handleCloseAdd = () => {
    setShiftAdd(false);
    setSelectedAddEmployee(null);
    setSelectedAddDate(null);
    setSelectedAddDepartment(null);
  };

  const handleDelete = async (shiftId) => {
    try {
      await apiDelete(`${apiPath.shiftDelete}/${shiftId}`);
      getAllShifts();
    } catch (error) {
      console.log("Delete error:", error);
    }
  };

  // ═══ UPDATED: Reset filters AND reset Calendar Date ═══
  const handleReset = () => {
    setFilterData({
      locationId: "",
      status: "",
      searchKey: "",
      startDate: "",
      endDate: "",
      isReset: true,
      shiftId: "",
      departmentId: ""
    });
    setPage(1);
    setSearchTerm("");
    setSelectedShift("");
    setDepartmentId("");
    
    // Move Calendar back to Today
    setCalendarDate(dayjs()); 
  };

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      startDate: dayjs(start).format("YYYY-MM-DD"),
      endDate: dayjs(end).format("YYYY-MM-DD"),
      isReset: false,
    });
  };

  // ═══ NEW: Reset Filters when switching to Calendar Tab ═══
  const handleSwitchToCalendar = () => {
    handleReset();
  };

  // Calendar Helpers
  const handleCalendarNav = (direction) => {
    if (calendarView === "daily") setCalendarDate(calendarDate.add(direction, "day"));
    else if (calendarView === "weekly") setCalendarDate(calendarDate.add(direction, "week"));
    else setCalendarDate(calendarDate.add(direction, "month"));
  };
  const goToToday = () => setCalendarDate(dayjs());
  const getCalendarDateRange = () => {
    if (calendarView === "daily") return calendarDate.format("MMMM D, YYYY");
    else if (calendarView === "weekly") {
      const start = calendarDate.startOf("week");
      const end = calendarDate.endOf("week");
      return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    } else return calendarDate.format("MMMM YYYY");
  };

  // ═══ API FETCHES ═══
  const getAllShifts = async () => {
    try {
      const { locationId, status, startDate, endDate, searchKey, shiftId, departmentId } = filterData;
      const payload = { page, pageSize, keyword: searchKey?.trim() };
      
      if (locationId && locationId !== 'undefined' && locationId !== 'null' && locationId !== '') payload.locationId = locationId;
      if (status && status !== 'undefined' && status !== 'null' && status !== '') payload.status = status;
      if (startDate && startDate !== '' && startDate !== 'undefined' && startDate !== 'null') payload.startDate = startDate;
      if (endDate && endDate !== '' && endDate !== 'undefined' && endDate !== 'null') payload.endDate = endDate;
      if (shiftId && shiftId !== '' && shiftId !== 'undefined' && shiftId !== 'null') payload.shiftId = shiftId;
      if (departmentId && departmentId !== '' && departmentId !== 'undefined' && departmentId !== 'null') payload.departmentId = departmentId;
      
      const result = await apiGet(apiPath.getAllShifts, payload);
      const response = result?.data?.results;

      if (Array.isArray(response)) {
        setShiftList(response);
        setPaginationObj({ ...paginationObj, page: 1, pageCount: 1, perPageItem: response.length, totalItems: response.length });
      } else if (response?.docs) {
        setShiftList(response.docs);
        setPaginationObj({ ...paginationObj, page: response.page, pageCount: response.totalPages, perPageItem: response.docs.length, totalItems: response.totalDocs });
      } else setShiftList([]);
    } catch (error) {
      console.log("error in get shifts==>>>>", error.message);
      setShiftList([]);
    }
  };

  const getLocations = async () => {
    try {
      const result = await apiGet(apiPath.getLocations, {});
      setLocations(result?.data?.results || []);
    } catch (error) {
      console.log("error in get locations==>>>>", error.message);
    }
  };

  const fetchAllShiftsForDropdown = async () => {
    try {
      const result = await apiGet(apiPath.getShiftTemplates, { page: 1, pageSize: 100 });
      const response = result?.data?.results;
      if (Array.isArray(response)) setAllShiftsList(response);
      else if (response?.docs) setAllShiftsList(response.docs);
    } catch (error) {
      console.log("error fetching shifts for dropdown", error);
    }
  };

  const getDepartments = async () => {
    try {
      const result = await apiGet(apiPath.getDepartmentList, {});
      setDepartments(result?.data?.results || []);
    } catch (error) {
      console.log("error in get departments==>>>>", error.message);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const params = {};
      if (filterData.startDate) params.startDate = filterData.startDate;
      if (filterData.endDate) params.endDate = filterData.endDate;
      if (filterData.departmentId) params.departmentId = filterData.departmentId;
      const result = await apiGet(apiPath.getAvailableEmployeesList, params);
      setAllEmployees(result?.data?.results || []);
    } catch (error) {
      console.log("error fetching all employees", error);
      setAllEmployees([]);
    }
  };

  // ═══ USE EFFECTS ═══
  useEffect(() => { getAllShifts(); }, [page, filterData]);
  useEffect(() => { getDepartments(); }, []);
  useEffect(() => { getLocations(); }, []);
  useEffect(() => { fetchAllShiftsForDropdown(); }, []);
  useEffect(() => { fetchAllEmployees(); }, [filterData.startDate, filterData.endDate, filterData.departmentId]);

  // ═══ RENDER ═══
  return (
    <div className="bg-[#F9F9F9]">
      <div className="px-3 py-4">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          
          {/* ═══ HEADER & FILTERS COMPONENT ═══ */}
          <ShiftFiltersHeader
            viewMode={viewMode}
            setViewMode={setViewMode}
            onSwitchToCalendar={handleSwitchToCalendar}
            calendarView={calendarView}
            setCalendarView={setCalendarView}
            calendarDate={calendarDate}
            setCalendarDate={setCalendarDate}
            handleCalendarNav={handleCalendarNav}
            goToToday={goToToday}
            getCalendarDateRange={getCalendarDateRange}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterData={filterData}
            setFilterData={setFilterData}
            setPage={setPage}
            allShiftsList={allShiftsList}
            departments={departments}
            locations={locations}
            selectedShift={selectedShift}
            setSelectedShift={setSelectedShift}
            departmentId={departmentId}
            setDepartmentId={setDepartmentId}
            setShowSwapRequests={setShowSwapRequests}
            setShowTemplateModal={setShowTemplateModal}
            setShiftAdd={setShiftAdd}
            setShowLocationModal={setShowLocationModal}
            handleReset={handleReset}
            handleDateChange={handleDateChange}
            t={t}
          />

          {/* ═══ CONTENT AREA ═══ */}
          {viewMode === "list" ? (
            <ShiftList
              shiftList={shiftList}
              page={page}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              currentItem={currentItem}
              getShiftColor={getShiftColor}
            />
          ) : (
            <ShiftCalendar
              shiftList={shiftList}
              employeeList={allEmployees}
              handleEdit={handleEdit}
              viewMode={calendarView}
              currentDate={calendarDate}
              onCardClick={handleCardClick}
              getShiftColor={getShiftColor}
            />
          )}

          {/* ═══ MODALS ═══ */}
          {shiftEdit && (
            <ShiftEdit
              handleEdit={setShiftEdit}
              currentItem={currentItem}
              allShifts={getAllShifts}
              locations={locations}
            />
          )}

          {shiftAdd && (
            <ShiftAdd
              allShifts={getAllShifts}
              handleCategory={handleCloseAdd}
              locations={locations}
              preSelectedEmployee={selectedAddEmployee}
              preSelectedDate={selectedAddDate}
              preSelectedDepartment={selectedAddDepartment}
            />
          )}

          <LocationManage
            show={showLocationModal}
            handleClose={() => setShowLocationModal(false)}
            locations={locations}
            refreshLocations={getLocations}
          />

          <SwapRequestsModal
            show={showSwapRequests}
            handleClose={() => setShowSwapRequests(false)}
            refreshShifts={getAllShifts}
          />

          <TemplateManage
            show={showTemplateModal}
            handleClose={() => setShowTemplateModal(false)}
            locations={locations}
          />

          {helpers.ternaryCondition(
            paginationObj?.totalItems && viewMode === "list",
            <Pagination
              handlePageClick={handlePageClick}
              options={paginationObj}
              page={page}
            />,
            null
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeShiftScheduling;